import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Award, TrendingUp, Clock, Users, CheckCircle, DollarSign,
  Target, Zap, BookOpen, Star, AlertCircle, Calendar,
  Phone as PhoneIcon, Mail, ArrowRight, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCHOLARSHIP_ENDPOINT = 'https://marketia.app.n8n.cloud/webhook/calculador-de-beca';

const ScholarshipInfo = ({ userPhone }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scholarshipData, setScholarshipData] = useState(null);

  useEffect(() => {
    const fetchScholarshipInfo = async () => {
      if (!userPhone) {
        setError('No se encontró número de teléfono en tu perfil');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        const response = await axios.post(SCHOLARSHIP_ENDPOINT, {
          telefono: userPhone
        });

        // La respuesta es un objeto directo, no un array
        if (response.data && response.data.data) {
          const data = JSON.parse(response.data.data);
          setScholarshipData(data);
        } else {
          setError('No se encontró información de beca para tu número');
        }
      } catch (err) {
        console.error('Error fetching scholarship:', err);
        console.error('Error details:', err.response?.data);
        setError(`Error al cargar información de beca: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchScholarshipInfo();
  }, [userPhone]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        <div className="text-center">
          <p className="text-white font-medium mb-1">Analizando tus datos...</p>
          <p className="text-gray-400 text-sm">Estamos calculando tu información de beca personalizada</p>
          <p className="text-gray-500 text-xs mt-2">Esto puede tomar unos segundos</p>
        </div>
      </div>
    );
  }

  if (error || !scholarshipData) {
    // Si no hay teléfono, mostrar mensaje especial
    if (!userPhone) {
      return (
        <div className="max-w-xl mx-auto">
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
            <div className="flex items-start space-x-3 mb-4">
              <PhoneIcon className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-bold mb-1">Agrega tu número de teléfono</h3>
                <p className="text-gray-300 text-sm">
                  Para ver tu información de beca necesitamos tu número de teléfono.
                </p>
              </div>
            </div>
            
            <button
              onClick={() => {
                window.location.href = '/app/profile';
              }}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 text-white text-sm font-bold py-2.5 px-4 rounded-lg transition-all"
            >
              <PhoneIcon className="w-4 h-4" />
              <span>Ir a Mi Perfil</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }
    
    // No se encontró información para este número
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6">
          <div className="flex items-start space-x-3 mb-4">
            <AlertCircle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-bold mb-1">No encontramos tu información de beca</h3>
              <p className="text-gray-400 text-sm mb-2">
                Número registrado: <span className="text-teal-400 font-mono">{userPhone}</span>
              </p>
              <p className="text-gray-300 text-sm mb-3">
                Esto puede pasar si aún no has iniciado o completado la conversación por WhatsApp para solicitar tu beca.
              </p>
              <p className="text-gray-300 text-sm">
                Contacta por WhatsApp para iniciar o completar tu solicitud, o edita tu número si es incorrecto.
              </p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <a
              href="https://wa.me/14702972453"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2.5 px-4 rounded-lg transition-all"
            >
              <PhoneIcon className="w-4 h-4" />
              <span>WhatsApp</span>
            </a>
            
            <button
              onClick={() => {
                window.location.href = '/app/profile';
              }}
              className="flex-1 flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-bold py-2.5 px-4 rounded-lg transition-all"
            >
              <PhoneIcon className="w-4 h-4" />
              <span>Editar Número</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { 
    badgeBeca, 
    tagline, 
    citaInspiracional, 
    elegibilidad, 
    beca,
    transformacion,
    proyeccion,
    roi,
    beneficios,
    urgencia,
    contacto,
    graficos
  } = scholarshipData;

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-teal-600 to-yellow-500 p-1">
        <div className="bg-gray-900 rounded-2xl p-8 md:p-12">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-purple-500/20 border border-purple-500/30 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <span className="text-purple-300 font-bold text-sm">{badgeBeca}</span>
          </div>

          {/* Tagline */}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {tagline}
          </h1>

          {/* Quote */}
          <p className="text-gray-300 italic text-lg border-l-4 border-teal-500 pl-4">
            "{citaInspiracional}"
          </p>

          {/* Score Badge */}
          <div className="mt-8 flex items-center space-x-4">
            <div className="bg-teal-500/20 border-2 border-teal-500 rounded-full w-24 h-24 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-400">{elegibilidad.score}</div>
                <div className="text-xs text-gray-400">Score</div>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Prioridad</div>
              <div className="inline-flex items-center space-x-2 bg-red-500/20 border border-red-500/30 px-3 py-1 rounded-full">
                <Star className="w-4 h-4 text-red-400" />
                <span className="text-red-300 font-bold text-sm">{elegibilidad.prioridad}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desglose de Beca */}
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <Award className="w-6 h-6 text-yellow-400" />
          <span>Desglose de tu Beca</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl p-4 text-center">
            <div className="text-gray-400 text-sm mb-2">Valor Completo</div>
            <div className="text-2xl font-bold text-white">{beca.desglose.valorCompleto}</div>
          </div>
          <div className="bg-gradient-to-br from-purple-600/20 to-teal-600/20 border border-purple-500/30 rounded-xl p-4 text-center">
            <div className="text-gray-400 text-sm mb-2">Beca ({beca.desglose.porcentaje})</div>
            <div className="text-2xl font-bold text-purple-300">{beca.desglose.montoBecado}</div>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
            <div className="text-gray-400 text-sm mb-2">Tu Inversión</div>
            <div className="text-2xl font-bold text-yellow-400">{beca.desglose.inversionEstudiante}</div>
          </div>
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 text-center">
            <div className="text-gray-400 text-sm mb-2">Ahorro Total</div>
            <div className="text-2xl font-bold text-teal-400">{beca.desglose.ahorro}</div>
          </div>
        </div>

        <div className="bg-teal-500/10 border border-teal-500/20 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-gray-300 font-medium mb-2">{beca.descripcion}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Opciones de Pago */}
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <DollarSign className="w-6 h-6 text-green-400" />
          <span>Opciones de Inversión</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Plan 3 Cuotas */}
          <div className="bg-gray-800 border border-white/10 rounded-xl p-6 hover:border-teal-500/50 transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">{beca.opciones.planCuotas.nombre}</h3>
              <div className="text-2xl font-bold text-teal-400">{beca.opciones.planCuotas.total}</div>
            </div>

            <div className="space-y-3 mb-6">
              {beca.opciones.planCuotas.cuotas.map((cuota, idx) => (
                <div key={idx} className="bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Cuota {cuota.numero}</span>
                    <span className="text-lg font-bold text-white">{cuota.monto}</span>
                  </div>
                  <div className="text-sm text-gray-500 mb-1">{cuota.momento}</div>
                  <div className="text-xs text-teal-400">{cuota.beneficio}</div>
                </div>
              ))}
            </div>

            <ul className="space-y-2">
              {beca.opciones.planCuotas.ventajas.map((ventaja, idx) => (
                <li key={idx} className="flex items-start space-x-2 text-sm text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>{ventaja}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Pago Único - Destacado */}
          <div className="relative bg-gradient-to-br from-yellow-600/20 to-orange-600/20 border-2 border-yellow-500/50 rounded-xl p-6 hover:border-yellow-500 transition-all">
            {/* Badge Recomendado */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-4 py-1 rounded-full text-xs font-bold">
              ⭐ RECOMENDADO
            </div>

            <div className="flex items-center justify-between mb-4 mt-2">
              <h3 className="text-xl font-bold text-white">{beca.opciones.pagoUnico.nombre}</h3>
              <div>
                <div className="text-sm text-gray-400 line-through">{beca.opciones.pagoUnico.precioRegular}</div>
                <div className="text-2xl font-bold text-yellow-400">{beca.opciones.pagoUnico.precioFinal}</div>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-400 mb-1">Ahorro Inmediato</div>
              <div className="text-3xl font-bold text-yellow-400">{beca.opciones.pagoUnico.ahorro}</div>
              <div className="text-xs text-gray-500 mt-1">{beca.opciones.pagoUnico.descuento} de descuento</div>
            </div>

            <ul className="space-y-2">
              {beca.opciones.pagoUnico.ventajas.map((ventaja, idx) => (
                <li key={idx} className="flex items-start space-x-2 text-sm text-gray-300">
                  <Star className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <span>{ventaja}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Proyección de Crecimiento */}
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <TrendingUp className="w-6 h-6 text-teal-400" />
          <span>Tu Transformación en 12 Meses</span>
        </h2>

        <div className="space-y-4">
          {proyeccion.timeline.map((milestone, idx) => (
            <div key={idx} className="bg-gray-800 rounded-xl p-6 hover:bg-gray-750 transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-teal-500/20 border border-teal-500 rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-400 font-bold">M{milestone.mes}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-1">{milestone.hito}</h3>
                    {milestone.hitoClave && (
                      <div className="inline-flex items-center space-x-1 bg-yellow-500/20 px-2 py-1 rounded text-xs text-yellow-400">
                        <Zap className="w-3 h-3" />
                        <span>{milestone.hitoClave}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-teal-400">{milestone.ingresosProyectados}</div>
                  <div className="text-xs text-gray-500">Ingresos proyectados</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-white/10">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Competencias</div>
                  <div className="text-lg font-bold text-white">{milestone.competenciasAdquiridas}%</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Proyectos</div>
                  <div className="text-lg font-bold text-white">{milestone.proyectosCompletados}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">AI Lab</div>
                  <div className="text-lg font-bold text-white">{milestone.proyectosAILab}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Inversión</div>
                  <div className="text-lg font-bold text-white">{milestone.inversionAcumulada}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ROI Summary */}
        <div className="mt-6 bg-gradient-to-r from-green-600/20 to-teal-600/20 border border-green-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-sm text-gray-400 mb-1">Incremento de Ingresos en 6 Meses</div>
              <div className="text-2xl font-bold text-green-400">
                {proyeccion.ingresos.proyectado6Meses} <span className="text-sm text-gray-400">({proyeccion.ingresos.porcentajeCrecimiento6M})</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Incremento en 12 Meses</div>
              <div className="text-2xl font-bold text-teal-400">
                {proyeccion.ingresos.proyectado12Meses} <span className="text-sm text-gray-400">({proyeccion.ingresos.porcentajeCrecimiento12M})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Beneficios */}
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
          <Target className="w-6 h-6 text-purple-400" />
          <span>Beneficios Exclusivos</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {beneficios.map((categoria, idx) => (
            <div key={idx} className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center space-x-2">
                <BookOpen className="w-5 h-5 text-teal-400" />
                <span>{categoria.categoria}</span>
              </h3>
              <ul className="space-y-2">
                {categoria.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="flex items-start space-x-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Urgencia */}
      <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border-2 border-red-500/50 rounded-2xl p-8">
        <div className="flex items-start space-x-4 mb-6">
          <div className="bg-red-500/20 p-3 rounded-full">
            <Clock className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white mb-2">¡Última Oportunidad!</h2>
            <p className="text-gray-300 mb-4">
              {urgencia.cupos} - La convocatoria cierra el {urgencia.fechaLimite}
            </p>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <div className="text-3xl font-bold text-red-400 mb-1">{urgencia.horasRestantes}</div>
              <div className="text-sm text-gray-400">Horas restantes para asegurar tu cupo</div>
            </div>
          </div>
        </div>

        <ul className="space-y-3 mb-6">
          {urgencia.razones.map((razon, idx) => (
            <li key={idx} className="flex items-start space-x-3 text-gray-300">
              <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <span>{razon}</span>
            </li>
          ))}
        </ul>

        {/* CTAs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href={`https://wa.me/${contacto.whatsapp.replace(/[^0-9]/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg hover:shadow-green-500/50"
          >
            <PhoneIcon className="w-5 h-5" />
            <span>Contactar por WhatsApp</span>
            <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href={`mailto:${contacto.email}`}
            className="flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold py-4 px-6 rounded-xl transition-all"
          >
            <Mail className="w-5 h-5" />
            <span>Enviar Email</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ScholarshipInfo;
